/* global document, sessionStorage, location, URLSearchParams */
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';

import Footer from './components/Footer/Footer';
import Header from './components/common/Header/Header';
import Main from './components/common/Main';
import { ALL } from './const/filters';
import { TITLE } from './const/sort';
import TopNav from './routes/home/TopNav/TopNav';
import './styles/index.less';

const App = () => {
	const [isdarkMode, setIsdarkMode] = useState(
		() => sessionStorage.getItem('isdarkMode') !== 'false',
	);
	const [isGuitarMode, setIsGuitarMode] = useState(
		() => sessionStorage.getItem('isGuitarMode') === 'true',
	);
	const [includesBarChord, setIncludesBarChord] = useState(true);
	const [genre, setGenre] = useState(ALL);
	const [sortTerm, setSort] = useState(TITLE);
	const [difficulty, setDifficulty] = useState(ALL);
	// The difficulty filter modal (owned here so a song row's difficulty bar can
	// open the same modal the nav's Difficulty control uses).
	const [isDifficultyOpen, setIsDifficultyOpen] = useState(false);
	// Pages with a sticky top band beside the logo (home + chords controls, song
	// title/artist); other pages reserve header space for the logo instead.
	const { pathname } = useLocation();
	const hasTopNav =
		['/', '/chords'].includes(pathname) || pathname.startsWith('/song/');

	// Splash the logo up from the middle of the screen and fade the rest of the
	// content in behind it - once per session, on whichever page is loaded first,
	// so later navigations and same-tab reloads skip it. Add `?splash` to the URL
	// to force it again (handy for testing). Cleared when the fade finishes (see
	// onAnimationEnd) so the one-shot fade can't re-apply to controls that remount
	// later - e.g. leaving and returning to the home page.
	const [isSplash, setIsSplash] = useState(() => {
		const forceSplash = new URLSearchParams(location.search).has('splash');
		const hasSplashed = sessionStorage.getItem('hasSplashed') === 'true';
		sessionStorage.setItem('hasSplashed', 'true');

		return forceSplash || !hasSplashed;
	});

	const onToggleIsDarkMode = () => {
		setIsdarkMode(!isdarkMode);
	};

	const onToggleIsGuitarMode = () => {
		setIsGuitarMode(!isGuitarMode);
		setIncludesBarChord(true);
	};

	const onToggleIncludesBarChord = () => {
		setIncludesBarChord(!includesBarChord);
	};

	const handleSort = (option: string) => {
		setSort(option);
	};

	useEffect(() => {
		sessionStorage.setItem('isdarkMode', String(isdarkMode));

		if (isdarkMode) {
			document.body.classList.add('dark-mode');
			document.body.classList.remove('light-mode');
		} else {
			document.body.classList.add('light-mode');
			document.body.classList.remove('dark-mode');
		}
	}, [isdarkMode]);

	useEffect(() => {
		sessionStorage.setItem('isGuitarMode', String(isGuitarMode));
	}, [isGuitarMode]);

	// useLayoutEffect (not useEffect) so the class is applied before the first
	// paint, avoiding a flash of the wrong header height on a fresh page load.
	useLayoutEffect(() => {
		document.body.classList.toggle('nav-collapsed', !hasTopNav);
	}, [hasTopNav]);

	const topNav = (
		<TopNav
			currentDifficulty={difficulty}
			currentGenre={genre}
			currentSort={sortTerm}
			includesBarChord={includesBarChord}
			isDifficultyOpen={isDifficultyOpen}
			isGuitarMode={isGuitarMode}
			isdarkMode={isdarkMode}
			onDifficulty={setDifficulty}
			onDifficultyOpenChange={setIsDifficultyOpen}
			onGenre={setGenre}
			onSort={handleSort}
			onToggleIncludesBarChord={onToggleIncludesBarChord}
			onToggleIsDarkMode={onToggleIsDarkMode}
			onToggleIsGuitarMode={onToggleIsGuitarMode}
		/>
	);

	return (
		<div
			className={isSplash ? 'max-container is-splashing' : 'max-container'}
			id="content"
			onAnimationEnd={(event) => {
				// Once the splash content fade-in ends, drop is-splashing so the
				// one-shot fade never re-applies to elements that remount later.
				if (event.animationName === 'content-fade-in') setIsSplash(false);
			}}
		>
			<Header isSplash={isSplash} topNav={topNav} />
			<Main
				currentDifficulty={difficulty}
				currentGenre={genre}
				currentSortTerm={sortTerm}
				includesBarChord={includesBarChord}
				isGuitarMode={isGuitarMode}
				onOpenDifficultyFilter={() => setIsDifficultyOpen(true)}
			/>
			<Footer isGuitarMode={isGuitarMode} />
		</div>
	);
};

createRoot(document.getElementById('app')).render(
	<BrowserRouter>
		<App />
	</BrowserRouter>,
);
